import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import resolveImageUrl from '../utils/resolveImageUrl';
import { getCategoryName } from '../utils/adminFields';
import { formatMoney } from '../utils/rentalFields';

/* Helpers */

const normalize = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');

function deriveYear(product) {
  const source = [product?.year, product?.modelYear, product?.name, product?.description, ...(product?.features || [])]
    .filter(Boolean)
    .join(' ');
  const match = source.match(/\b(20\d{2}|19\d{2})\b/);
  return match ? match[1] : '';
}

function getVehicleImage(product) {
  const imageValue = product?.imageURL || product?.imageUrl || product?.image || product?.photo || product?.thumbnail;
  if (!imageValue) return '';
  try { return resolveImageUrl(imageValue); } catch { return imageValue; }
}

function vehiclePath(product) {
  if (!product?.id) return '/vehicles';
  return `/vehicle/show/${product.id}/${encodeURIComponent(product.name || 'vehicle')}`;
}

function normalizeVehicle(product, categoryNameById) {
  const category = product.categoryName || categoryNameById.get(String(product.categoryId)) || product.category?.categoryName || 'Premium Vehicle';
  const seats = Number(product.seats || product.capacity || product.passengerCapacity || 5);
  const transmission = product.transmission || product.gearbox || 'Automatic';
  const availability = product.bookingStatus || product.booking_status || product.availability || 'Available';
  return {
    ...product,
    id: product.id,
    name: product.name || 'Premium Vehicle',
    year: deriveYear(product),
    price: Number(product.price || 0),
    category,
    availability,
    bookingStatus: availability,
    transmission,
    seats: Number.isFinite(seats) && seats > 0 ? seats : 5,
    description: product.description || 'Premium DeRoyalty vehicle available for daily hire and executive travel.',
    features: Array.isArray(product.features) ? product.features : [],
    imageSrc: getVehicleImage(product),
  };
}

function getVehicleFeatures(vehicle) {
  const features = Array.isArray(vehicle?.features) ? vehicle.features.filter(Boolean).map(String) : [];
  const base = [
    vehicle?.year && `${vehicle.year} model`,
    vehicle?.category,
    vehicle?.transmission,
  ].filter(Boolean);
  return Array.from(new Set([...features, ...base])).slice(0, 6);
}

function getStatusMeta(status) {
  const raw = String(status || 'Available').trim();
  const key = normalize(raw);
  if (!raw || key === 'available') return { label: 'Available', tone: 'available' };
  if (key.includes('reserved') || key.includes('rented') || key.includes('booking confirmed')) return { label: 'Reserved', tone: 'reserved' };
  if (key.includes('checked out') || key.includes('in use')) return { label: 'In Use', tone: 'in-use' };
  return { label: raw, tone: 'reserved' };
}

/* Main Component */

function CompareVehicles({ products, categories }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');

  const categoryNameById = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((category) => {
      map.set(String(category.id), getCategoryName(category));
    });
    return map;
  }, [categories]);

  const allVehicles = useMemo(() => {
    const apiVehicles = Array.isArray(products) ? products : [];
    return apiVehicles.map((product) => normalizeVehicle(product, categoryNameById));
  }, [products, categoryNameById]);

  const compareIdsFromUrl = useMemo(
    () => (searchParams.get('compare') || '').split(',').map((id) => id.trim()).filter(Boolean),
    [searchParams]
  );

  const compareVehicles = useMemo(
    () => allVehicles.filter((vehicle) => compareIdsFromUrl.includes(String(vehicle.id))),
    [compareIdsFromUrl, allVehicles]
  );

  const maxPrice = useMemo(() => {
    if (!compareVehicles.length) return 1;
    return Math.max(...compareVehicles.map((v) => v.price), 1);
  }, [compareVehicles]);

  const toggleVehicle = (vehicleId) => {
    const id = String(vehicleId);
    const currentIds = new Set(compareIdsFromUrl);
    if (currentIds.has(id)) {
      currentIds.delete(id);
    } else {
      currentIds.add(id);
    }
    const next = Array.from(currentIds);
    const params = new URLSearchParams(searchParams);
    if (next.length) {
      params.set('compare', next.join(','));
    } else {
      params.delete('compare');
    }
    setSearchParams(params, { replace: true });
  };

  const removeVehicle = (vehicleId) => {
    toggleVehicle(vehicleId);
  };

  const clearAll = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('compare');
    setSearchParams(params, { replace: true });
  };

  const filteredAvailable = useMemo(() => {
    if (!searchQuery.trim()) return allVehicles;
    const tokens = normalize(searchQuery).split(' ').filter(Boolean);
    return allVehicles.filter((v) => {
      const text = normalize(`${v.name} ${v.category} ${v.year} ${v.description}`);
      return tokens.every((t) => text.includes(t));
    });
  }, [allVehicles, searchQuery]);

  const specRows = [
    {
      label: 'Status',
      icon: 'bi-circle-fill',
      render: (v) => {
        const meta = getStatusMeta(v.availability);
        return <span className={`rio-cmp-status-badge ${meta.tone}`}>{meta.label}</span>;
      },
    },
    {
      label: 'Daily Rate',
      icon: 'bi-cash-stack',
      render: (v) => (
        <div className="rio-cmp-price-cell">
          <strong>{formatMoney(v.price)}</strong>
          <span>/ day</span>
          <div className="rio-cmp-price-bar">
            <div
              className="rio-cmp-price-bar-fill"
              style={{ width: `${Math.round((v.price / maxPrice) * 100)}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      label: 'Category',
      icon: 'bi-tag',
      render: (v) => v.category,
    },
    {
      label: 'Model Year',
      icon: 'bi-calendar3',
      render: (v) => v.year || 'Not listed',
    },
    {
      label: 'Seats',
      icon: 'bi-people',
      render: (v) => (
        <div className="rio-cmp-seats-cell">
          <span className="rio-cmp-seats-count">{v.seats}</span>
          <span className="rio-cmp-seats-label">seats</span>
        </div>
      ),
    },
    {
      label: 'Transmission',
      icon: 'bi-gear',
      render: (v) => v.transmission,
    },
  ];

  const hasComparison = compareVehicles.length >= 1;

  return (
    <div className="booking-page rio-compare-page">
      <div className="rio-compare-shell">
        {/* Breadcrumb */}
        <nav className="rio-breadcrumb">
          <Link to="/home" className="rio-breadcrumb-link">
            <i className="bi bi-house-door" aria-hidden="true" />
            <span>Home</span>
          </Link>
          <i className="bi bi-chevron-right rio-breadcrumb-sep" aria-hidden="true" />
          <Link to="/vehicles" className="rio-breadcrumb-link">Vehicles</Link>
          <i className="bi bi-chevron-right rio-breadcrumb-sep" aria-hidden="true" />
          <span className="rio-breadcrumb-current">Compare</span>
        </nav>

        {/* Page Header */}
        <header className="rio-cmp-header">
          <div className="rio-cmp-header-icon">
            <i className="bi bi-arrow-left-right" aria-hidden="true" />
          </div>
          <div className="rio-cmp-header-text">
            <span className="rio-eyebrow">Vehicle Insights</span>
            <h1 className="rio-cmp-title">Compare Vehicles</h1>
            <p className="rio-cmp-subtitle">
              {hasComparison
                ? `Comparing ${compareVehicles.length} vehicle${compareVehicles.length === 1 ? '' : 's'} - select more from the fleet below to add to comparison.`
                : 'Select vehicles from the fleet below to compare specs, pricing, features, and availability side by side.'}
            </p>
          </div>
          {hasComparison && (
            <div className="rio-cmp-header-actions">
              <button type="button" className="btn-luxury-outline rio-action-btn" onClick={clearAll}>
                <i className="bi bi-x-lg" aria-hidden="true" />
                Clear All
              </button>
            </div>
          )}
        </header>

        {/* Comparison Dashboard */}
        {hasComparison && (
          <section className="rio-cmp-dashboard">
            {/* Vehicle Header Cards */}
            <div className="rio-cmp-vehicle-headers" style={{ gridTemplateColumns: `180px repeat(${compareVehicles.length}, minmax(200px, 1fr))` }}>
              <div className="rio-cmp-corner-label">
                <i className="bi bi-bezier2" aria-hidden="true" />
                <span>Specification</span>
              </div>
              {compareVehicles.map((vehicle) => (
                <div key={vehicle.id} className="rio-cmp-vehicle-card">
                  <button
                    type="button"
                    className="rio-cmp-remove-btn"
                    onClick={() => removeVehicle(vehicle.id)}
                    aria-label={`Remove ${vehicle.name}`}
                  >
                    <i className="bi bi-x" />
                  </button>
                  <div className="rio-cmp-vehicle-img-wrap">
                    {vehicle.imageSrc ? (
                      <img src={vehicle.imageSrc} alt={vehicle.name} />
                    ) : (
                      <div className="rio-cmp-vehicle-img-placeholder">
                        <i className="bi bi-car-front" />
                      </div>
                    )}
                  </div>
                  <strong className="rio-cmp-vehicle-card-name">{vehicle.name}</strong>
                  <span className="rio-cmp-vehicle-card-cat">{vehicle.category}</span>
                </div>
              ))}
            </div>

            {/* Spec Rows */}
            <div className="rio-cmp-spec-rows">
              {specRows.map((row) => (
                <div
                  key={row.label}
                  className="rio-cmp-spec-row"
                  style={{ gridTemplateColumns: `180px repeat(${compareVehicles.length}, minmax(200px, 1fr))` }}
                >
                  <div className="rio-cmp-spec-label">
                    <i className={`bi ${row.icon}`} aria-hidden="true" />
                    <span>{row.label}</span>
                  </div>
                  {compareVehicles.map((vehicle) => (
                    <div key={`${vehicle.id}-${row.label}`} className="rio-cmp-spec-value">
                      {row.render(vehicle)}
                    </div>
                  ))}
                </div>
              ))}

              {/* Features Row */}
              <div
                className="rio-cmp-spec-row rio-cmp-features-row"
                style={{ gridTemplateColumns: `180px repeat(${compareVehicles.length}, minmax(200px, 1fr))` }}
              >
                <div className="rio-cmp-spec-label">
                  <i className="bi bi-stars" aria-hidden="true" />
                  <span>Key Features</span>
                </div>
                {compareVehicles.map((vehicle) => (
                  <div key={`${vehicle.id}-features`} className="rio-cmp-spec-value">
                    <ul className="rio-cmp-feature-tags">
                      {getVehicleFeatures(vehicle).map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Action Row */}
              <div
                className="rio-cmp-spec-row rio-cmp-action-row"
                style={{ gridTemplateColumns: `180px repeat(${compareVehicles.length}, minmax(200px, 1fr))` }}
              >
                <div className="rio-cmp-spec-label">
                  <i className="bi bi-arrow-right-circle" aria-hidden="true" />
                  <span>Next Step</span>
                </div>
                {compareVehicles.map((vehicle) => (
                  <div key={`${vehicle.id}-action`} className="rio-cmp-spec-value rio-cmp-action-cell">
                    <Link to={vehiclePath(vehicle)} className="btn-luxury rio-action-btn">
                      <i className="bi bi-eye" aria-hidden="true" />
                      View Details
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Vehicle Selector */}
        <section className="rio-cmp-selector">
          <div className="rio-cmp-selector-header">
            <div>
              <h2 className="rio-section-label">
                <i className="bi bi-plus-circle" aria-hidden="true" />
                {hasComparison ? 'Add More Vehicles' : 'Select Vehicles to Compare'}
              </h2>
              <p className="rio-cmp-selector-hint">
                Click on vehicles to {hasComparison ? 'add them to' : 'start'} your comparison.
              </p>
            </div>
            <div className="rio-cmp-search-wrap">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search vehicles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rio-cmp-search-input"
              />
            </div>
          </div>

          <div className="rio-cmp-selector-grid">
            {filteredAvailable.map((vehicle) => {
              const isSelected = compareIdsFromUrl.includes(String(vehicle.id));
              return (
                <button
                  key={vehicle.id}
                  type="button"
                  className={`rio-cmp-selector-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleVehicle(vehicle.id)}
                  aria-pressed={isSelected}
                >
                  <div className="rio-cmp-selector-check">
                    <i className={`bi ${isSelected ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                  </div>
                  <div className="rio-cmp-selector-img">
                    {vehicle.imageSrc ? (
                      <img src={vehicle.imageSrc} alt={vehicle.name} />
                    ) : (
                      <div className="rio-cmp-vehicle-img-placeholder">
                        <i className="bi bi-car-front" />
                      </div>
                    )}
                  </div>
                  <div className="rio-cmp-selector-info">
                    <strong>{vehicle.name}</strong>
                    <span>{vehicle.category}</span>
                    <span className="rio-cmp-selector-price">{formatMoney(vehicle.price)}/day</span>
                  </div>
                </button>
              );
            })}
          </div>

          {filteredAvailable.length === 0 && (
            <div className="rio-cmp-empty">
              <i className="bi bi-search" />
              <p>No vehicles found matching "{searchQuery}"</p>
            </div>
          )}
        </section>

        {/* Footer CTA */}
        <div className="rio-cmp-footer">
          <Link to="/vehicles" className="btn-luxury-outline rio-action-btn">
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to All Vehicles
          </Link>
        </div>
      </div>
    </div>
  );
}

export default CompareVehicles;
